import type { Account } from "./types";

/**
 * "As-of" balance lookups. Balances are keyed by ISO date (YYYY-MM-DD): the
 * daily model lets Cowork snapshot an account many times (once a day, or more),
 * each landing as its own point instead of overwriting a single monthly slot.
 * Historical monthly history was relabeled to month-end dates by the migration.
 *
 * Net worth on a given date is therefore an *as-of* sum: each account
 * contributes its most-recent observation on or before that date — accounts
 * aren't all snapshotted on the same day, so we carry each one's last value
 * forward until it's refreshed.
 */

/** The account's most recent observation date, or undefined if never observed. */
export function latestKey(a: Account): string | undefined {
  let best: string | undefined;
  for (const k in a.balances) if (best === undefined || k > best) best = k;
  return best;
}

/** Balance of `a` as of `date` (inclusive): the value at the latest key ≤ date,
 * or undefined if the account had no observation on or before `date`. */
export function balanceAsOf(a: Account, date: string): number | undefined {
  let best: string | undefined;
  for (const k in a.balances) if (k <= date && (best === undefined || k > best)) best = k;
  return best === undefined ? undefined : a.balances[best];
}

/** Contribution of `a` to an aggregate as of `date`. Like balanceAsOf, but a
 * closed account (active === false) contributes nothing after its final
 * observation — it doesn't carry forward the way an open account does, so a
 * rolled-over 401k or paid-off loan drops out instead of lingering. */
export function contributionAsOf(a: Account, date: string): number {
  if (a.active === false) {
    const max = latestKey(a);
    if (max !== undefined && date > max) return 0;
  }
  return balanceAsOf(a, date) ?? 0;
}

/* ── Sweeps ─────────────────────────────────────────────────────────────────
 * balanceAsOf/contributionAsOf scan every balance key per call, which is fine
 * for a point lookup but O(dates × keys) when charting a whole series — and
 * the daily-balance model makes both factors big. These helpers walk the date
 * axis once, carrying each account's last-seen value forward.
 * ------------------------------------------------------------------------ */

/** balanceAsOf across an ASCENDING date axis: one value per date (null before
 * the account's first observation). Sparkline/series-friendly. */
export function balanceSeries(a: Account, dates: string[]): (number | null)[] {
  const keys = Object.keys(a.balances).sort();
  let idx = 0;
  let cur: number | null = null;
  return dates.map((d) => {
    while (idx < keys.length && keys[idx] <= d) {
      cur = a.balances[keys[idx]];
      idx++;
    }
    return cur;
  });
}

/** contributionAsOf for every account across an ASCENDING date axis. Returns
 * one row per date, one entry per account (aligned with `accounts`). */
export function contributionMatrix(accounts: Account[], dates: string[]): number[][] {
  const states = accounts.map((a) => ({
    a,
    keys: Object.keys(a.balances).sort(),
    idx: 0,
    cur: undefined as number | undefined,
  }));
  return dates.map((d) =>
    states.map((s) => {
      while (s.idx < s.keys.length && s.keys[s.idx] <= d) {
        s.cur = s.a.balances[s.keys[s.idx]];
        s.idx++;
      }
      // Closed accounts stop carrying forward after their final observation.
      const closed =
        s.a.active === false && s.keys.length > 0 && s.idx >= s.keys.length && d > s.keys[s.keys.length - 1];
      return closed ? 0 : s.cur ?? 0;
    }),
  );
}
